<template>
  <Component
    :is="tag"
    ref="container"
    :class="{ 'paving-item-container': init }"
    :style="styles"
  >
    <slot />
  </Component>
</template>

<script lang="ts" setup>
interface Props {
  itemList: any[]
  tag?: string
  cols?: number
  columnWidth?: number
  margin?: number[]
  minColumn?: number
}
const props = withDefaults(defineProps<Props>(), {
  tag: 'div',
  cols: 0,
  columnWidth: 160,
  margin: () => [0, 0],
  minColumn: 2,
})

const container = ref(null)

const init = ref(false)

const styles = computed(() => {
  if (!init.value) {
    return {
      display: 'grid',
      height: 'auto',
      'column-gap': `${props.margin[0]}px`,
      'row-gap': `${props.margin[1]}px`,
      'grid-template-columns': `repeat( auto-fill, minmax( ${props.columnWidth}px, 1fr ) )`,
    }
  }
})

let localItems = []
let columnCount = props.minColumn
let timeOutId

const marginX = computed(() => {
  return props.margin[0]
})

const marginY = computed(() => {
  return props.margin[1]
})

const updateItemList = () => {
  if (!container.value) {
    return false
  }
  setTimeout(() => {
    if (!container.value) return
    const items = container.value.children
    for (let i = 0; i < props.itemList.length; i++) {
      if (!items[i]) {
        return false
      }
      if (!(items[i] instanceof HTMLElement)) {
        continue
      }
      let imgs = items[i].querySelectorAll('img')
      imgs = Array.prototype.slice.call(imgs, 0)
      imgs.forEach(img => {
        img.addEventListener('load', setGridPosition)
      })
      localItems[i] = items[i]
    }
    setGridPosition()
  }, 250)
}

const setColumnCount = () => {
  if (!container.value) {
    return false
  }
  const columnWidth = props.columnWidth + marginX.value
  columnCount = Math.max(
    props.cols || Math.floor(container.value.clientWidth / columnWidth),
    props.minColumn,
  )
}

const setGridPosition = () => {
  if (!container.value) {
    return false
  }
  const bottoms = []
  for (let i = 0; i < columnCount; i++) {
    bottoms.push(0)
  }
  const width = `calc(
      ${100 / bottoms.length}% - ${
        (marginX.value * (bottoms.length - 1)) / bottoms.length
      }px
    )`
  for (let i = 0; i < props.itemList.length; i++) {
    const min = bottoms.indexOf(Math.min.apply(null, bottoms))
    if (!localItems[i]) {
      return false
    }
    localItems[i].style.width = width
    localItems[i].style.top = bottoms[min] + 'px'
    localItems[i].style.left = `calc(
        ${(100 / bottoms.length) * min}%
        + ${marginX.value * (1 - (bottoms.length - 1) / bottoms.length) * min}px
      )`
    localItems[i].style.visibility = 'visible'
    bottoms[min] += localItems[i].clientHeight + marginY.value
  }
  container.value.style.height = Math.max.apply(null, bottoms) + 'px'
  init.value = true
}

const loop = () => {
  // 検出できないDOMの変更に対応するため
  timeOutId = setTimeout(() => {
    setColumnCount()
    setGridPosition()
    loop()
  }, 250)
}

onMounted(() => {
  setColumnCount()
  updateItemList()
  setTimeout(loop, 1000)
})

onUnmounted(() => {
  clearTimeout(timeOutId)
})

watch(() => props.itemList, updateItemList, { deep: true })

watch(() => columnCount, setGridPosition)
</script>

<style lang="scss" scoped>
.paving-item-container {
  position: relative;
  > * {
    /* width, heightもアニメーションするとポジション計算に影響するのでleft, topのみ指定 */
    transition:
      left 0.6s,
      top 0.6s ease;
    position: absolute;
    visibility: hidden;
  }
}
</style>
